import { IAceEditor } from 'react-ace/lib/types';
import { Dependency } from '../../static/react-injector/src/injectables/modules/Components/api/models/dependency.model';
import camelCase from 'lodash/camelCase';

declare global {
    interface DE {
        aceUtils: typeof aceUtils;
    }
}

const aceUtils = {
    createCompleter: createAceCompleter,
    getModeForLanguage: getAceModeForLanguage,
};

window.de.aceUtils = aceUtils;

export function createAceCompleter(
    editor: IAceEditor,
    dataSources: Dependency[] = [],
    functions: Dependency[] = [],
) {
    const completer = {
        // eslint-disable-next-line
        identifierRegexps: [/[a-zA-Z_0-9.\$\-\u00A2-\uFFFF]/],
        // @ts-ignore
        getCompletions(editor, session, pos, prefix, callback) {
            let key = 'root';

            if (prefix.indexOf('.') > 0) {
                const splited = prefix.split('.');
                key = splited
                    .slice(0, splited.length - 1)
                    .join('.')
                    .toLowerCase();
            }

            // pad captions to max length, but difference between items should not be more than max_pads
            const max_pads = 10;
            const items = tables[key] || [];
            items.forEach((i) => (i.caption = i.caption.trim()));
            const maxLineLength = Math.max(...items.map((i) => i.caption.length));
            items.forEach((i) => {
                i.caption = i.caption.padRight(
                    Math.min(i.caption.length + max_pads, maxLineLength),
                );
            });

            callback(null, items);
        },
        insertMatch(editor: any, data: any) {
            if (data?.onSelect) {
                data.onSelect(data, (data: any) => applyAceAutocompleteItem(editor, data));
            } else {
                applyAceAutocompleteItem(editor, data);
            }
        },
    };

    const tables = createAutocompleteTables(dataSources || [], 'ds');
    createFunctionsAutocompletes(functions, tables);

    function createAutocompleteTables(
        dependencies: Dependency[],
        meta: string,
        tables: { [key: string]: any } = {},
        onSelect?: (item: any, insertAtCursorFn: (data: any) => any) => any,
        captionProperty: keyof Dependency = 'Path',
        name = 'root',
    ) {
        name = name.toLowerCase();

        dependencies.forEach((row) => {
            tables[name] = [
                ...(tables[name] || []),
                {
                    caption: row[captionProperty],
                    value: row.Path,
                    originalData: row,
                    meta: meta,
                    completer: completer,
                    onSelect: onSelect,
                },
            ];

            if (row.Children?.length) {
                createAutocompleteTables(
                    row.Children,
                    meta,
                    tables,
                    onSelect,
                    captionProperty,
                    row.Path,
                );
            }
        });

        return tables;
    }

    function createFunctionsAutocompletes(functions: Dependency[] = [], tables = {}) {
        // lowercase module variable
        functions = lowerCaseFunctions(functions);

        createAutocompleteTables(
            functions,
            'func',
            tables,
            handleFunctionSelected,
            'OriginalPath' as any,
        );

        function handleFunctionSelected(item: any, insertAtCursorFn: (data: any) => void) {
            // import module only on function/method select
            if (item.originalData.Children?.length > 0) {
                insertAtCursorFn(item);
                return;
            }

            const moduleName = item.originalData.PathItems[0].module;
            const variableName = camelCase(moduleName);
            const newText = item.originalData.PathItems.map((item) =>
                item.module ? variableName : item.PropertyName,
            ).join('.');

            insertAtCursorFn(newText);

            const expression = `const ${variableName} = require('${moduleName}');`;
            if (!editor.getValue().includes(expression)) {
                editor.getSession().insert({ row: 0, column: 0 }, expression + '\n');
            }
        }

        function lowerCaseFunctions(functionsList: Dependency[]) {
            return functionsList.map((func) => {
                return {
                    ...func,
                    OriginalPath: func.Path,
                    Path: lowerCaseModuleVariable(func.Path),
                    Children: (func.Children || []).map((child) => ({
                        ...child,
                        OriginalPath: child.Path,
                        Path: lowerCaseModuleVariable(child.Path),
                    })),
                };
            });

            function lowerCaseModuleVariable(path: string) {
                return path
                    .split('.')
                    .map((v, index) => (index === 0 ? v[0].toLowerCase() + v.slice(1) : v))
                    .join('.');
            }
        }
    }

    return completer;
}

export function getAceModeForLanguage(language: string) {
    language = language?.toLowerCase() || 'javascript';

    switch (language) {
        case 'robot':
            return 'robot_framework_syntax_highlighting';
        case 'dotnet':
            return 'csharp';
        default:
            return language;
    }
}

function applyAceAutocompleteItem(editor: IAceEditor, data: any) {
    const completer = editor.completer;

    if (completer?.completions?.filterText) {
        const ranges = completer.editor.selection.getAllRanges();

        ranges.forEach((range: any) => {
            range.start.column -= completer.completions.filterText.length;
            completer.editor.session.remove(range);
        });
    }

    editor.execCommand('insertstring', data?.value || data);
}
